// The floating feedback launcher, pinned bottom-right on every page while an
// Argo employee is impersonating — the same placement ArgoWMS uses, so filing a
// report never costs the reporter their place on the page.
//
// Gated on an active impersonation session because that is the backend's gate
// too (routes/feedback.ts): a customer would only ever see a refusal.

import { useRef, useState } from 'react';

import { useAuth } from '../../auth/AuthContext.js';
import { CAPTURE_EXCLUDE_ATTR } from './captureScreenshot.js';
import { FeedbackDialog } from './FeedbackDialog.js';

export function FeedbackLauncher(): JSX.Element | null {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  if (auth.status !== 'signed_in' || !auth.impersonation) return null;

  // Closing unmounts the dialog's focused node, which would drop a keyboard
  // user back at <body>.
  function close(): void {
    setOpen(false);
    buttonRef.current?.focus();
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="feedback-launcher"
        onClick={() => setOpen(true)}
        title="Report a problem or request an improvement to the portal"
        aria-label="Give feedback"
        aria-haspopup="dialog"
        {...{ [CAPTURE_EXCLUDE_ATTR]: '' }}
      >
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>
      <FeedbackDialog open={open} onClose={close} />
    </>
  );
}
